import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  DollarSign, 
  MapPin,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Level 1: Services that have state fees
const SERVICE_TYPES_WITH_STATE_FEES = [
  { value: 'llc_formation', label: 'LLC Formation' },
  { value: 'c_corp_formation', label: 'C-Corp Formation' },
  { value: 's_corp_formation', label: 'S-Corp Formation' },
  { value: 'amendment', label: 'Amendments' },
];

// Level 2: Amendment sub-types (shown when Amendments tab is active)
const AMENDMENT_SUB_TYPES = [
  { value: 'name_change', label: 'Name Change' },
  { value: 'address_change', label: 'Address Change' },
  { value: 'registered_agent_change', label: 'Registered Agent Change' },
  { value: 'director_member_change', label: 'Director/Member Change' },
  { value: 'share_structure_change', label: 'Share Structure Change' },
  { value: 'certificate_good_standing', label: 'Certificate of Good Standing' },
  { value: 'franchise_tax', label: 'Franchise Tax Filing' },
  { value: 'annual_report', label: 'Annual Report' },
  { value: 'foreign_qualification', label: 'Foreign Qualification' },
  { value: 'dissolution', label: 'Dissolution' },
];

// Level 3: Entity types for amendments
const ENTITY_TYPES = [
  { value: 'llc', label: 'LLC' },
  { value: 'c_corp', label: 'C-Corp' },
  { value: 's_corp', label: 'S-Corp' },
];

// All 50 US states
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const SuperAdminStateFees = () => {
  const { token } = useAuth();
  const [allFees, setAllFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('llc_formation');
  const [countryView, setCountryView] = useState('US');
  const [activeSubType, setActiveSubType] = useState('name_change'); // Default sub-type for amendments
  const [activeEntityType, setActiveEntityType] = useState('llc'); // Default entity type for amendments
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const fetchFees = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (countryView !== 'US') {
        // Foreign country view: fetch all that country's fee rows
        params.append('country', countryView);
      } else {
        // US view: filter by the active service-type tab
        params.append('service_type', activeTab);
        if (activeTab === 'amendment') {
          params.append('sub_type', activeSubType);
          params.append('entity_type', activeEntityType);
        }
      }
      
      const response = await axios.get(`${API}/admin/state-fees?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const feesData = response.data?.state_fees || response.data || [];
      setAllFees(feesData);
    } catch (err) {
      console.error('Error fetching state fees:', err);
      if (!allFees.length) {
        setError('Failed to load state fees');
      }
    } finally {
      setLoading(false);
    }
  };

  // Refetch when tab, sub-type, entity-type, or country changes
  useEffect(() => {
    fetchFees();
  }, [token, activeTab, activeSubType, activeEntityType, countryView]);

  // Save a fee directly by id (used by the foreign-country table)
  const [editingFeeId, setEditingFeeId] = useState(null);
  const [editFeeValue, setEditFeeValue] = useState('');
  const handleSaveFeeById = async (feeId, label) => {
    setSavingId(feeId);
    setError('');
    try {
      await axios.put(`${API}/admin/state-fees/${feeId}`, {
        govt_fee: parseFloat(editFeeValue) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      showSuccess(`${label} fee updated`);
      setEditingFeeId(null);
      setEditFeeValue('');
      fetchFees();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update fee'));
    } finally {
      setSavingId(null);
    }
  };
  
  // When switching to amendments tab, ensure sub-type and entity-type are set
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Reset to defaults when switching to amendments
    if (newTab === 'amendment') {
      setActiveSubType('name_change');
      setActiveEntityType('llc');
    }
  };
  
  // When switching sub-type, reset entity type to LLC
  const handleSubTypeChange = (newSubType) => {
    setActiveSubType(newSubType);
    setActiveEntityType('llc');
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Get fees for the currently selected service type (and sub-type if applicable)
  // Since we now fetch filtered data from API, allFees IS the filtered result
  const feesForCurrentService = allFees;

  // Get fee for a specific state within current service type
  const getFeeForState = (stateName, stateCode) => {
    return feesForCurrentService.find(
      f => f.state === stateName || f.state === stateCode || f.state_code === stateCode
    );
  };

  // Start editing
  const handleStartEdit = (stateCode, currentFee) => {
    setEditingId(stateCode);
    // Use govt_fee field (correct field name from backend)
    setEditValue(currentFee?.govt_fee?.toString() || '0');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  // Save fee
  const handleSaveFee = async (stateCode, stateName) => {
    const fee = getFeeForState(stateName, stateCode);
    
    if (!fee) {
      // No existing fee record - would need to create one
      // For now, show message
      setError(`No fee record exists for ${stateName}. Please seed state fees first.`);
      setEditingId(null);
      return;
    }

    setSavingId(stateCode);
    setError('');
    
    try {
      await axios.put(`${API}/admin/state-fees/${fee.id}`, {
        govt_fee: parseFloat(editValue) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showSuccess(`${stateName} fee updated`);
      setEditingId(null);
      setEditValue('');
      fetchFees();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update fee'));
    } finally {
      setSavingId(null);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e, stateCode, stateName) => {
    if (e.key === 'Enter') {
      handleSaveFee(stateCode, stateName);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Current count from fetched data
  const currentCount = allFees.length;

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      {/* Success Toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-[#00E87A] text-black rounded-lg shadow-lg">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Back Link */}
      <Link
        to="/superadmin/products"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Products
      </Link>

      {/* Page Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">State Fees</h1>
          <p className="text-gray-400 text-sm mt-1">
            Manage government filing fees by jurisdiction. Click on a fee to edit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 uppercase tracking-wider">Country</span>
          <select
            value={countryView}
            onChange={(e) => setCountryView(e.target.value)}
            className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
            data-testid="statefees-country-select"
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="AE">United Arab Emirates</option>
            <option value="GB">United Kingdom</option>
            <option value="SG">Singapore</option>
          </select>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
            Dismiss
          </button>
        </div>
      )}

      {/* Level 1: Service Type Tabs (US only) */}
      {countryView === 'US' && (
      <div className="flex gap-2 mb-4 border-b border-gray-800 overflow-x-auto pb-px">
        {SERVICE_TYPES_WITH_STATE_FEES.map(service => {
          const isActive = activeTab === service.value;
          
          return (
            <button
              key={service.value}
              onClick={() => handleTabChange(service.value)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors relative ${
                isActive 
                  ? 'text-[#00E87A]' 
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid={`tab-${service.value}`}
            >
              {service.label}
              {isActive && !loading && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[#00E87A]/20">
                  {currentCount}
                </span>
              )}
              {isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00E87A]" />
              )}
            </button>
          );
        })}
      </div>
      )}

      {/* Level 2: Amendment Sub-Type Tabs (only shown when Amendments is active) */}
      {activeTab === 'amendment' && (
        <div className="flex gap-1 mb-4 overflow-x-auto pb-2">
          {AMENDMENT_SUB_TYPES.map(subType => {
            const isActive = activeSubType === subType.value;
            
            return (
              <button
                key={subType.value}
                onClick={() => handleSubTypeChange(subType.value)}
                className={`px-3 py-2 text-xs font-medium whitespace-nowrap rounded-md transition-colors ${
                  isActive 
                    ? 'bg-[#00E87A] text-black' 
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
                data-testid={`subtab-${subType.value}`}
              >
                {subType.label}
              </button>
            );
          })}
        </div>
      )}
      
      {/* Level 3: Entity Type Tabs (only shown when Amendments is active) */}
      {activeTab === 'amendment' && (
        <div className="flex gap-2 mb-6">
          {ENTITY_TYPES.map(entityType => {
            const isActive = activeEntityType === entityType.value;
            
            return (
              <button
                key={entityType.value}
                onClick={() => setActiveEntityType(entityType.value)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors border ${
                  isActive 
                    ? 'border-[#00E87A] bg-[#00E87A]/10 text-[#00E87A]' 
                    : 'border-gray-700 text-gray-400 hover:border-gray-600 hover:text-white'
                }`}
                data-testid={`entitytab-${entityType.value}`}
              >
                {entityType.label}
                {isActive && !loading && (
                  <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-[#00E87A]/20">
                    {currentCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      
      {/* Spacer when no sub-tabs */}
      {activeTab !== 'amendment' && <div className="mb-2" />}

      {/* State Fees Table (US grid) */}
      {countryView === 'US' && (
      <div className="bg-[#161B22] border border-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">State</th>
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Code</th>
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Government Fee (USD)</th>
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {US_STATES.map((state) => {
                const fee = getFeeForState(state.name, state.code);
                const feeAmount = fee?.govt_fee || 0;
                const hasFee = !!fee;
                const isEditing = editingId === state.code;
                const isSaving = savingId === state.code;
                
                return (
                  <tr key={state.code} className="hover:bg-gray-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span className="text-white">{state.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-400 font-mono">{state.code}</span>
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, state.code, state.name)}
                              onBlur={() => handleSaveFee(state.code, state.name)}
                              autoFocus
                              min="0"
                              step="0.01"
                              className="w-28 pl-7 pr-3 py-1.5 bg-[#0D1117] border border-[#00E87A] rounded text-white text-sm focus:outline-none"
                              data-testid={`fee-input-${state.code}`}
                            />
                          </div>
                          {isSaving && <Loader2 className="w-4 h-4 text-[#00E87A] animate-spin" />}
                        </div>
                      ) : hasFee ? (
                        <button
                          onClick={() => handleStartEdit(state.code, fee)}
                          className="flex items-center gap-1 text-white hover:text-[#00E87A] transition-colors group"
                          data-testid={`fee-btn-${state.code}`}
                        >
                          <DollarSign className="w-4 h-4 text-gray-500 group-hover:text-[#00E87A]" />
                          <span className="font-medium">{feeAmount.toFixed(2)}</span>
                        </button>
                      ) : (
                        <span className="text-gray-500 text-sm">Not configured</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {hasFee ? (
                        <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-[#00E87A]/20 text-[#00E87A]">
                          Configured
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-gray-600/20 text-gray-400">
                          Not Set
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Foreign country fee table (CA / AE / GB / SG) */}
      {countryView !== 'US' && (
      <div className="bg-[#161B22] border border-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Region</th>
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Entity Type</th>
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Government Fee (USD)</th>
                <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {allFees.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-gray-500">No formation fees configured for this country.</td></tr>
              )}
              {[...allFees].sort((a, b) => (a.state || '').localeCompare(b.state || '') || (a.entity_type || '').localeCompare(b.entity_type || '')).map((fee) => {
                const isEditing = editingFeeId === fee.id;
                const isSaving = savingId === fee.id;
                return (
                  <tr key={fee.id} className="hover:bg-gray-800/30 transition-colors" data-testid={`foreign-fee-row-${fee.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span className="text-white">{fee.state}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className="text-gray-300 font-mono text-xs">{fee.entity_type || '—'}</span></td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                            <input
                              type="number" value={editFeeValue}
                              onChange={(e) => setEditFeeValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveFeeById(fee.id, fee.state); if (e.key === 'Escape') { setEditingFeeId(null); setEditFeeValue(''); } }}
                              onBlur={() => handleSaveFeeById(fee.id, fee.state)}
                              autoFocus min="0" step="0.01"
                              className="w-28 pl-7 pr-3 py-1.5 bg-[#0D1117] border border-[#00E87A] rounded text-white text-sm focus:outline-none"
                              data-testid={`foreign-fee-input-${fee.id}`}
                            />
                          </div>
                          {isSaving && <Loader2 className="w-4 h-4 text-[#00E87A] animate-spin" />}
                        </div>
                      ) : (
                        <button
                          onClick={() => { setEditingFeeId(fee.id); setEditFeeValue((fee.govt_fee ?? 0).toString()); }}
                          className="flex items-center gap-1 text-white hover:text-[#00E87A] transition-colors group"
                          data-testid={`foreign-fee-btn-${fee.id}`}
                        >
                          <DollarSign className="w-4 h-4 text-gray-500 group-hover:text-[#00E87A]" />
                          <span className="font-medium">{(fee.govt_fee ?? 0).toFixed(2)}</span>
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell"><span className="text-gray-500 text-xs">{fee.notes || '—'}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Help Text */}
      <div className="mt-6 p-4 bg-[#161B22] border border-gray-800 rounded-lg">
        <h3 className="text-white font-medium mb-2">How State Fees Work</h3>
        <ul className="text-gray-400 text-sm space-y-1">
          <li>• Select a service type tab to view fees for that service</li>
          <li>• For Amendments: select the amendment type (row 2), then entity type (row 3)</li>
          <li>• Amendment fees vary by: state × sub-type × entity type (e.g., Franchise Tax for Wyoming LLC)</li>
          <li>• State fees are added to the product price at order time</li>
          <li>• Click on any fee amount to edit it inline</li>
          <li>• Press Enter to save or Escape to cancel</li>
        </ul>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminStateFees;
